import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { startOfDay, getTime } from 'date-fns';
import { Day } from './day';
import { cellStyles } from './view/styles';
import { CalendarEvents } from '../types';

export class DatePickerCalendarCellWebComponent extends LitElement {
  @property({ type: Object }) day: Day;
  @property({ type: Boolean, reflect: true }) selected = false;
  @property({ type: Boolean, reflect: true }) startDate = false;
  @property({ type: Boolean, reflect: true }) endDate = false;
  @property({ type: Boolean, reflect: true }) hovered = false;
  @property({ type: Number }) dateTo;
  @property({ type: Number }) dateFrom;
  @property({ type: String }) month;
  @property({ type: Number }) min;
  @property({ type: Number }) max;
  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) protected hoveredDate;
  @property({ type: Boolean }) protected isCurrentDate = false;

  static get styles() {
    return cellStyles;
  }

  render(): TemplateResult {
    return html`
      <div
        role="button"
        aria-selected="${this.selected}"
        aria-disabled="${this.disabled}"
        tabindex="${this.disabled ? -1 : 0}"
        @click="${this.handleTap}"
        @mouseover="${this.handleHover}"
        class="day ${this.isCurrentDate ? 'currentDate' : ''} ${this.isSelected(this.selected)} ${this.isHovered(
          this.hovered
        )} ${this.isEnabled(this.day, this.min, this.max)}"
      >
        <div class="currentDayMarker">${this.day?.title}</div>
      </div>
    `;
  }

  updated(properties: PropertyValues): void {
    if (
      properties.has('dateFrom') ||
      properties.has('dateTo') ||
      properties.has('hoveredDate') ||
      properties.has('day')
    ) {
      this.dateChanged(this.dateFrom, this.dateTo, this.hoveredDate, this.day);
    }
  }

  dateChanged(dateFrom: string, dateTo: string, hoveredDate: number, day: Day): void {
    this.selected = false;
    this.hovered = false;
    this.startDate = false;
    this.endDate = false;
    const parsedDateFrom = parseInt(dateFrom, 10);
    const parsedDateTo = parseInt(dateTo, 10);
    if (day) {
      if (getTime(startOfDay(parsedDateTo * 1000)) / 1000 === day.date) {
        this.selected = true;
        this.endDate = true;
      }

      if (getTime(startOfDay(parsedDateFrom * 1000)) / 1000 === day.date) {
        this.selected = true;
        this.startDate = true;
      }

      if (
        ((hoveredDate === day.date || day.date < hoveredDate) &&
          day.date > parsedDateFrom &&
          !parsedDateTo &&
          !Number.isNaN(parsedDateFrom) &&
          parsedDateFrom !== undefined &&
          !this.selected) ||
        (day.date > parsedDateFrom && day.date < parsedDateTo)
      ) {
        this.hovered = true;
      }
    }
  }

  handleTap(): void {
    if (!this.disabled) {
      this.dispatchEvent(
        new CustomEvent(CalendarEvents.DATE_IS_SELECTED, {
          detail: { date: this.day?.date },
        })
      );
    }
  }

  handleHover(): void {
    this.dispatchEvent(
      new CustomEvent(CalendarEvents.DATE_IS_HOVERED, {
        detail: { date: this.day?.date },
      })
    );
  }

  isSelected(selected: boolean): string {
    return selected ? 'selected' : '';
  }

  isHovered(hovered: boolean): string {
    return hovered ? 'hovered' : '';
  }

  isEnabled(day: Day | null, min: number, max: number): string {
    this.disabled = false;

    if (day?.date) {
      const dayDate = startOfDay(day.date * 1000).getTime();
      const minDate = startOfDay(min * 1000).getTime();
      const maxDate = max === 0 ? 0 : startOfDay(max * 1000).getTime();

      if (dayDate >= minDate && (maxDate === 0 || dayDate <= maxDate)) {
        return ''; // Day is within the valid range, including min and max
      }
      this.disabled = true;
      return 'disabled';
    }
    return '';
  }
}
